import {
  NotFoundPage,
  RequireInternalOrganization,
  RequireOrganization,
} from '@startupquickstart/frontend';
import { createBrowserRouter } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { HomePage } from './pages/HomePage';
import { PersonalAccountHome } from './pages/PersonalAccountHome';

// Type pages
import {
  AmenityTypeDetailPage,
  AmenityTypeFormPage,
  AmenityTypesPage,
} from './pages/amenity-types';
import {
  AttractionCategoriesPage,
  AttractionCategoryDetailPage,
  AttractionCategoryFormPage,
} from './pages/attraction-categories';
import {
  AttractionTypeDetailPage,
  AttractionTypeFormPage,
  AttractionTypesPage,
} from './pages/attraction-types';
import { LanguageDetailPage, LanguageFormPage, LanguagesPage } from './pages/languages';
import { ThemeTypeDetailPage, ThemeTypeFormPage, ThemeTypesPage } from './pages/theme-types';
import { TourTypeDetailPage, TourTypeFormPage, TourTypesPage } from './pages/tour-types';

// Location pages
import {
  GreaterAreaDetailPage,
  GreaterAreaFormPage,
  GreaterAreasPage,
} from './pages/greater-areas';
import { HotelDetailPage, HotelFormPage, HotelsDashboardPage, HotelsPage } from './pages/hotels';
import {
  NeighborhoodDetailPage,
  NeighborhoodFormPage,
  NeighborhoodsPage,
} from './pages/neighborhoods';

// Content pages
import { AmenitiesPage, AmenityDetailPage, AmenityFormPage } from './pages/amenities';
import { AttractionDetailPage, AttractionFormPage, AttractionsPage } from './pages/attractions';
import { AttractionThemeDetailPage, AttractionThemeFormPage } from './pages/attractions/themes';
import {
  AttractionThemeTranslationDetailPage,
  AttractionThemeTranslationFormPage,
} from './pages/attractions/themes/translations';
import {
  AttractionTranslationDetailPage,
  AttractionTranslationFormPage,
} from './pages/attractions/translations';
import { TourDetailPage, TourFormPage, ToursPage } from './pages/tours';
import {
  TourTranslationDetailPage,
  TourTranslationFormPage,
} from './pages/tours/translations';

// Marketing pages
import {
  HotelAccessCodeDetailPage,
  HotelAccessCodeFormPage,
  HotelAccessCodesPage,
} from './pages/hotel-access-codes';
import {
  PromotionalOfferDetailPage,
  PromotionalOfferFormPage,
  PromotionalOffersPage,
} from './pages/promotional-offers';

// User pages
import { UserMePage } from './pages/users/UserMePage';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      // Personal account home page (mobile app)
      {
        path: 'home',
        element: <PersonalAccountHome />,
      },
      // Non-internal organization dashboard (hotel cards)
      {
        path: 'dashboard',
        element: (
          <RequireOrganization>
            <HotelsDashboardPage />
          </RequireOrganization>
        ),
      },
      // Tours - Internal Only
      {
        path: 'tours',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ToursPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tours/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tours/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tours/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Tour Translations - Internal Only (nested under tours)
      {
        path: 'tours/:id/translations/:translationId',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTranslationDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tours/:id/translations/:translationId/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTranslationFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attractions - Internal Only
      {
        path: 'attractions',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attraction Themes - Internal Only (nested under attractions)
      {
        path: 'attractions/:attractionId/themes/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionThemeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/:attractionId/themes/:themeId',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionThemeDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/:attractionId/themes/:themeId/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionThemeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attraction Theme Translations - Internal Only (nested under themes)
      {
        path: 'attractions/:attractionId/themes/:themeId/translations/:translationId',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionThemeTranslationDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/:attractionId/themes/:themeId/translations/:translationId/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionThemeTranslationFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attraction Translations - Internal Only (nested under attractions)
      {
        path: 'attractions/:attractionId/translations/:translationId',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTranslationDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/:attractionId/translations/:translationId/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTranslationFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Tour Types - Internal Only
      {
        path: 'tour-types',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTypesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tour-types/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tour-types/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTypeDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tour-types/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attraction Categories - Internal Only
      {
        path: 'attraction-categories',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionCategoriesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attraction-categories/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionCategoryFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attraction-categories/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionCategoryDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attraction-categories/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionCategoryFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attraction Types - Internal Only
      {
        path: 'attraction-types',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTypesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attraction-types/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attraction-types/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTypeDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attraction-types/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Amenity Types - Internal Only
      {
        path: 'amenity-types',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityTypesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'amenity-types/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'amenity-types/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityTypeDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'amenity-types/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Theme Types - Internal Only
      {
        path: 'theme-types',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ThemeTypesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'theme-types/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ThemeTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'theme-types/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ThemeTypeDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'theme-types/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ThemeTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Languages - Internal Only
      {
        path: 'languages',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <LanguagesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'languages/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <LanguageFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'languages/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <LanguageDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'languages/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <LanguageFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Greater Areas - Internal Only
      {
        path: 'greater-areas',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <GreaterAreasPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'greater-areas/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <GreaterAreaFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'greater-areas/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <GreaterAreaDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'greater-areas/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <GreaterAreaFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Neighborhoods - Internal Only
      {
        path: 'neighborhoods',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <NeighborhoodsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'neighborhoods/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <NeighborhoodFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'neighborhoods/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <NeighborhoodDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'neighborhoods/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <NeighborhoodFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Hotels - List/New/Edit are Internal Only, Detail uses wrapper
      {
        path: 'hotels',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'hotels/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'hotels/:id',
        element: (
          <RequireOrganization>
            <HotelDetailPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'hotels/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Promotional Offers - Accessible to both (filtered for non-internal)
      {
        path: 'promotional-offers',
        element: (
          <RequireOrganization>
            <PromotionalOffersPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'promotional-offers/new',
        element: (
          <RequireOrganization>
            <PromotionalOfferFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'promotional-offers/:id',
        element: (
          <RequireOrganization>
            <PromotionalOfferDetailPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'promotional-offers/:id/edit',
        element: (
          <RequireOrganization>
            <PromotionalOfferFormPage />
          </RequireOrganization>
        ),
      },
      // Hotel Access Codes - Internal Only (managed at hotel level)
      {
        path: 'hotel-access-codes',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelAccessCodesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'hotel-access-codes/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelAccessCodeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'hotel-access-codes/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelAccessCodeDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'hotel-access-codes/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelAccessCodeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Amenities - Internal Only (managed at hotel level)
      {
        path: 'amenities',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenitiesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'amenities/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'amenities/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'amenities/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'users/me',
        element: (
          <RequireOrganization>
            <UserMePage />
          </RequireOrganization>
        ),
      },
      // fallback
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
